import { message } from "antd";
import * as XLSX from "xlsx";
import { BanksValues, OtherBankValue } from "./utils";

const HEADER = ["Fecha", "Referencia", "Descripción", "Debe-Haber", "Saldo"];

// Función para convertir plantillas según el banco
export const convertTemplate = (data, bank) => {
	try {
		if (!data || !Array.isArray(data) || data.length === 0) {
			message.error("El archivo no contiene datos válidos.");
			return;
		}

		let result = [];
		switch (bank) {
		case BanksValues.BAM:
			result = __bancoAgromercantil(data);
			break;
		case BanksValues.GYT:
			result = __bancoGyT(data);
			break;
		case BanksValues.BAC:
			result = __bancoBAC(data);
			break;
		case BanksValues.BI:
			result = __bancoIndustrial(data);
			break;
		case BanksValues.INTERBANCO:
			result = __bancoInterBanco(data);
			break;
		case BanksValues.BANRURAL:
			result = __bancoBanrural(data);
			break;
		//case BanksValues.BANTRAB: // Bantrab Not Avaliable Yet
		//	// ...
		//	break;
		case OtherBankValue:
			result = __otherBank(data);
			break;
		default:
			message.error("Banco no soportado.");
			return;
		}

		// Si se obtiene un resultado válido, exportamos el Excel
		if (result.length > 0) {			
			return result;
		}
	} catch (error) {
		console.error("Error al procesar la plantilla:", error);
		message.error("Ocurrió un error al procesar la plantilla.");
	}
};

export const downloadXlsx = (data, bank, flag = "") => {
	if(flag === "color"){
		__exportToExcelWithColors(data, `${bank}.xlsx`);
		return;
	}
	
	__exportToExcel(data, `${bank}.xlsx`);
};

// Función para validar si el valor es una fecha (en formato de cadena o número de Excel)
function __esFechaValida(valor) {
	// Si es un número (fecha en formato Excel)
	if (typeof valor === "number") {
		return true;
	}
	// Si es una cadena, verificamos que sea una fecha en formato DD/MM/YYYY
	if (typeof valor === "string") {
		const regexFecha = /^\d{2}\/\d{2}\/\d{4}$/; // Regex para validar formato DD/MM/YYYY
		return regexFecha.test(valor);
	}
	return false;
}

function __excelDateToJSDate(serial) {
	const excelEpoch = new Date(Date.UTC(1899, 11, 30)); // 30 de diciembre de 1899 (ajustado para Excel)
	const date = new Date(excelEpoch.getTime() + serial * 86400 * 1000); // sumar días en milisegundos
	return date;
}


function __convertDateStringToISO(dateString) {
	// Reemplazar cualquier barra "/" con guión "-" para tratar ambos formatos
	const normalizedDateString = dateString.replace(/\//g, "-");

	// Asumimos que la fecha viene en formato "dia-mes-año" (dd-mm-yyyy)
	const [day, month, year] = normalizedDateString.split("-");

	// Crear una fecha en el formato correcto "yyyy-mm-dd"
	const isoFormattedDate = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;

	return isoFormattedDate;
}

function __bancoAgromercantil(data) {
	try {
		// Verificar que el formato de datos sea correcto
		if (!Array.isArray(data) || data.length === 0 || !Array.isArray(data[0])) {
			message.error(
				"La plantilla proporcionada no tiene el formato esperado para Banco Agromercantil."
			);
			return [];
		}

		const filas = data.slice(1); // Excluir la HEADER

		// Verificar que las filas tengan el número esperado de columnas
		const expectedColumns = 11; // Ajusta este número según tus necesidades
		if (filas.some((fila) => fila.length < expectedColumns)) {
			message.error(
				"La plantilla proporcionada no tiene el formato esperado para Banco Agromercantil."
			);
			return [];
		}

		let warningDate = false;

		const parseNumber = (value) => {
			if (typeof value === "number") {
				return value;
			}
			if (typeof value === "string") {
				// Eliminar caracteres no numéricos y reemplazar comas
				value = value.replace(/[^0-9,.-]/g, "").replace(/,/g, "");
				const parsedValue = parseFloat(value);
				return isNaN(parsedValue) ? 0 : parsedValue;
			}
			return 0;
		};

		const resultado = filas.map((fila, index) => {
			// Extraer campos individuales
			const fecha = fila[0];
			const documento = fila[2];
			//const referencia = fila[3];

			// Construir la descripción
			const descripcion = [
				fila[3] ?? "",
				fila[4] ?? "",
				fila[5] ?? "",
				fila[9] ?? "",
				fila[10] ?? "",
			].join("|");

			let credito = fila[6];
			let debito = fila[7];
			let saldo = fila[8];

			// Validar campos requeridos
			if (fecha == null || credito == null || debito == null || saldo == null) {
				throw new Error(
					`Formato de datos inválido en la fila ${index + 2
					}. Campos requeridos faltantes.`
				);
			}

			// Parsear campos numéricos
			credito = parseNumber(credito);
			debito = parseNumber(debito);
			saldo = parseNumber(saldo);

			if (isNaN(credito) || isNaN(debito) || isNaN(saldo)) {
				throw new Error(
					`Formato de datos inválido en la fila ${index + 2
					}. Créditos, débitos o saldo no son numéricos.`
				);
			}

			// Procesar la fecha
			let fechaStr;
			if (typeof fecha === "number") {
				const fechaObj = __excelDateToJSDate(fecha);
				fechaStr = fechaObj.toISOString().split("T")[0];
				warningDate = true;
			} else if (typeof fecha === "string") {
				fechaStr = __convertDateStringToISO(fecha);
			} else {
				throw new Error(
					`Formato de fecha no soportado en la fila ${index + 2}.`
				);
			}

			// Calcular el debe-haber: Débitos positivos, Créditos negativos
			const debeHaber = debito !== 0 ? debito : -credito;

			return [fechaStr, documento, descripcion, debeHaber, saldo];
		});

		if (warningDate) {
			message.info(
				"Su plantilla utiliza un formato de fecha que puede causar problemas de integridad, por favor convierta las fechas a texto",
				10
			);
		}
		
		return [HEADER, ...resultado];
	} catch (error) {
		console.error("Error al procesar datos de Banco Agromercantil:", error);
		message.error(
			"Error al procesar los datos del banco Agromercantil. Verifique el formato de la plantilla."
		);
		return [];
	}
}

function __bancoGyT(data) {
	try {
		// Verificar que el formato de datos sea correcto
		if (!Array.isArray(data) || data.length === 0 || !Array.isArray(data[0])) {
			message.error(
				"La plantilla proporcionada no tiene el formato esperado para Banco GyT."
			);
			return [];
		}

		// Buscar el índice de la fila de HEADER de las transacciones
		const indexCabecera = data.findIndex(
			(fila) => Array.isArray(fila) && fila.includes("Fecha")
		);

		if (indexCabecera === -1 || data.length <= indexCabecera + 1) {
			message.error(
				"No se encontraron datos de transacciones en el formato esperado."
			);
			return [];
		}

		// Extraer las filas de transacciones después de la HEADER
		const filas = data.slice(indexCabecera + 1).filter((fila) => {
			return (
				Array.isArray(fila) &&
				fila.length > 0 &&
				typeof fila[1] !== "undefined" &&
				fila[1] !== null &&
				fila[1] !== ""
			);
		});

		// Verificar que las filas tengan el número esperado de columnas
		const expectedColumns = 8; // Ajusta este número según tus necesidades
		if (filas.some((fila) => fila.length < expectedColumns)) {
			message.error(
				"La plantilla proporcionada no tiene el formato esperado para Banco GyT."
			);
			return [];
		}

		let warningDate = false;

		const parseNumber = (value) => {
			if (value == null || value === "") {
				return 0;
			}
			if (typeof value === "number") {
				return value;
			}
			if (typeof value === "string") {
				// Eliminar caracteres no numéricos y reemplazar comas
				value = value.replace(/[^0-9,.-]/g, "").replace(/,/g, "");
				const parsedValue = parseFloat(value);
				return isNaN(parsedValue) ? 0 : parsedValue;
			}
			return 0;
		};

		const resultado = filas.map((fila, index) => {
			const [
			/* Número */,
				fecha,
				referencia,
				descripcion1,
				debito,
				credito,
				saldo,
				/* Otros campos */ ...rest
			] = fila;

			// Construir la descripción concatenando todos los valores de 'rest'
			const descripcionAdicional = rest.filter((item) => item != null && item !== "").join("|");
			const descripcion = [descripcion1 ?? "", descripcionAdicional].filter((item) => item !== "").join("|").trim();

			// Validar campos requeridos
			if (
				fecha == null ||
				fecha === "" ||
				referencia == null ||
				referencia === "" ||
				descripcion == null ||
				descripcion === "" ||
				saldo == null ||
				saldo === ""
			) {
				throw new Error(
					`Formato de datos inválido en la fila ${index + indexCabecera + 2
					}. Campos requeridos faltantes.`
				);
			}

			// Procesar la fecha
			let fechaStr;
			if (typeof fecha === "number") {
				const fechaObj = __excelDateToJSDate(fecha);
				fechaStr = fechaObj.toISOString().split("T")[0];
				warningDate = true;
			} else if (typeof fecha === "string") {
				fechaStr = __convertDateStringToISO(fecha);
			} else {
				throw new Error(
					`Formato de fecha no soportado en la fila ${index + indexCabecera + 2
					}.`
				);
			}

			// Parsear campos numéricos
			const valorDebito = parseNumber(debito);
			const valorCredito = parseNumber(credito);
			const valorSaldo = parseNumber(saldo);

			// Calcular el valor de "Debe-Haber"
			const debeHaber = valorDebito !== 0 ? valorDebito : valorCredito;

			return [fechaStr, referencia, descripcion, debeHaber, valorSaldo];
		});

		if (warningDate) {
			message.info(
				"Su plantilla utiliza un formato de fecha que puede causar problemas de integridad, por favor convierta las fechas a texto",
				10
			);
		}

		// Agregamos la HEADER		
		return [HEADER, ...resultado];
	} catch (error) {
		console.error("Error al procesar datos de Banco GyT:", error);
		message.error(
			"Error al procesar los datos del banco GyT. Verifique el formato de la plantilla."
		);
		return [];
	}
}


function __bancoBAC(data) {
	try {
		// Verificar que el formato de datos sea correcto
		if (!Array.isArray(data) || data.length === 0 || !Array.isArray(data[0])) {
			message.error("La plantilla proporcionada no tiene el formato esperado para Banco BAC.");
			return [];
		}

		// Buscar el índice de la fila de HEADER de las transacciones
		const indexCabecera = data.findIndex((fila) => Array.isArray(fila) && fila.includes("Fecha") && fila.includes("Referencia"));

		if (indexCabecera === -1 || data.length <= indexCabecera + 1) {
			message.error("No se encontraron datos de transacciones en el formato esperado.");
			return [];
		}

		// Extraer las filas de transacciones después de la HEADER
		const filas = data
			.slice(indexCabecera + 1)
			.filter(fila => fila.length > 0 && __esFechaValida(fila[0])); // Filtrar solo transacciones con una fecha válida

		// Verificar que las filas tengan el número esperado de columnas
		if (filas.some(fila => fila.length < 7)) {
			message.error("La plantilla proporcionada no tiene el formato esperado para Banco BAC.");
			return [];
		}

		let warningDate = false;

		const resultado = filas.map((fila) => {
			const [fecha, referencia, descripcion, debito, credito] = [
				fila[0],   // Fecha
				fila[1],   // Referencia
				(fila[4] ?? "") + "|" + (fila[3] ?? ""),   // Descripción, verificando que no sea undefined
				fila[7] ?? "",   // Débito, verificando que no sea undefined
				fila[8] ?? "",    // Crédito, verificando que no sea undefined
				//fila[9] ?? 0
			];
			let saldo = fila[9] ?? 0;

			// Validar que los valores críticos sean válidos
			if (typeof fecha !== "string" || typeof referencia !== "string" || typeof descripcion !== "string") {
				throw new Error("Formato de datos inválido en la fila.");
			}

			// Identificar si la fecha es un número (formato Excel) o una cadena (formato DD/MM/YYYY)
			let fechaStr;
			if (typeof fecha === "number") {
				// Convertir desde el formato Excel a una fecha
				const fechaObj = __excelDateToJSDate(fecha);
				fechaStr = fechaObj.toISOString().split("T")[0]; // Convertir a formato YYYY-MM-DD
				warningDate = true;
			} else if (typeof fecha === "string") {
				// Asumimos que la fecha está en formato DD/MM/YYYY
				fechaStr = __convertDateStringToISO(fecha);
			} else {
				throw new Error("Formato de fecha no soportado.");
			}

			// Convertir valores de crédito/débito a números
			const valorDebito = parseFloat(debito) || 0;
			const valorCredito = parseFloat(credito) || 0;

			// Calcular el valor de "Debe-Haber"
			const debeHaber = valorDebito !== 0 ? -valorDebito : valorCredito;

			if (saldo instanceof String) {
				saldo = parseFloat(saldo.replace(",", ""));
			}

			return [fechaStr, referencia, descripcion.trim(), debeHaber, saldo];
		});

		if (warningDate) message.info("Su plantilla utiliza un formato de fecha que puede causar problemas de integridad, por favor convierta las fechas a texto", 10);

		// Agregamos la HEADER		
		return [HEADER, ...resultado];

	} catch (error) {
		console.error("Error al procesar datos de Banco BAC:", error);
		message.error("Error al procesar los datos del banco BAC. Verifique el formato de la plantilla.");
		return [];
	}
}

// Función para procesar datos de InterBanco
function __bancoInterBanco(data) {
	try {
		// Verificar que el formato de datos sea correcto
		if (!Array.isArray(data) || data.length === 0 || !Array.isArray(data[0])) {
			message.error("La plantilla proporcionada no tiene el formato esperado para InterBanco.");
			return [];
		}

		let warningDate = false;

		const resultado = data.map((fila) => {
			// hora
			const [fechaExcel, extra, descripcion, documento, debeHaber] = [
				fila[3],                             // Fecha (puede ser Excel o cadena)
				//fila[4],                             // Hora (no utilizada)
				fila[5] ?? "",                       // Extra (columna adicional, si existe)
				fila[6] ?? "",                       // Descripción
				fila[7] ?? "",                       // Documento
				fila[8] ?? "",                       // D (Débito) o H (Haber)
				//fila[9] ?? 0                        // Monto
			];
			let monto = fila[9] ?? 0;

			// Identificar si la fecha es un número (formato Excel) o una cadena (formato DD/MM/YYYY)
			let fechaStr;
			if (typeof fechaExcel === "number") {
				// Convertir desde el formato Excel a una fecha
				const fechaObj = __excelDateToJSDate(fechaExcel);
				fechaStr = fechaObj.toISOString().split("T")[0]; // Convertir a formato YYYY-MM-DD
				warningDate = true;
			} else if (typeof fechaExcel === "string") {
				// Asumimos que la fecha está en formato DD/MM/YYYY
				fechaStr = __convertDateStringToISO(fechaExcel);
			} else {
				throw new Error("Formato de fecha no soportado.");
			}

			// Construir la descripción combinando la descripción con la columna extra
			const descripcionCompleta = `${descripcion} | ${extra}`;

			if (monto instanceof String) {
				monto = parseFloat(monto.replace(",", ""));
			}

			// Calcular el debe-haber: Débitos positivos, Créditos negativos
			const valorDebeHaber = debeHaber === "D" ? -monto : monto;

			// Retornar la fila procesada en el formato correcto
			return [fechaStr, documento, descripcionCompleta.trim(), valorDebeHaber, 0];
		});

		if (warningDate) message.info("Su plantilla utiliza un formato de fecha que puede causar problemas de integridad, por favor convierta las fechas a texto", 10);
		
		return [HEADER, ...resultado];

	} catch (error) {
		console.error("Error al procesar datos de InterBanco:", error);
		message.error("Error al procesar los datos de InterBanco. Verifique el formato de la plantilla.");
		return [];
	}
}

// Función para procesar datos de Banco Industrial
function __bancoIndustrial(data) {
	try {
		// Verificar que el formato de datos sea correcto
		if (!Array.isArray(data) || data.length === 0 || !Array.isArray(data[0])) {
			message.error("La plantilla proporcionada no tiene el formato esperado para Banco Industrial.");
			return [];
		}

		// Encontrar el índice de la HEADER (donde empieza la tabla de transacciones)
		const indexCabecera = data.findIndex(fila => Array.isArray(fila) && fila.includes("Fecha"));
		if (indexCabecera === -1 || data.length <= indexCabecera + 1) {
			message.error("No se encontraron datos de transacciones en el formato esperado.");
			return [];
		}

		// Filtrar y procesar las filas que son transacciones reales
		const filas = data.slice(indexCabecera + 1).filter(fila => fila.length > 0 && fila[0]); // Ignorar filas vacías

		let warningDate = false;

		const resultado = filas.map((fila) => {
			const [fechaExcel, tipoTransaccion, descripcion, numeroDoc, debe, haber] = [
				fila[0],               // Fecha (puede ser Excel o cadena)
				fila[1] ?? "",         // TT (tipo de transacción)
				fila[2] ?? "",         // Descripción
				fila[3] ?? "",         // No. Doc
				fila[4] ?? 0,          // Debe (si existe)
				fila[5] ?? 0,           // Haber (si existe)
				//fila[6] ?? 0           // Haber (si existe)
			];
			let saldo = fila[6] ?? 0;

			// Identificar si la fecha es un número (formato Excel) o una cadena (formato DD/MM/YYYY)
			let fechaStr;
			if (typeof fechaExcel === "number") {
				// Convertir desde el formato Excel a una fecha
				const fechaObj = __excelDateToJSDate(fechaExcel);
				fechaStr = fechaObj.toISOString().split("T")[0]; // Convertir a formato YYYY-MM-DD
				warningDate = true;
			} else if (typeof fechaExcel === "string") {
				// Asumimos que la fecha está en formato DD/MM/YYYY
				fechaStr = __convertDateStringToISO(fechaExcel);
			} else {
				throw new Error("Formato de fecha no soportado.");
			}

			// Convertir valores de crédito/débito a números
			const valorDebito = parseFloat(debe) || 0;
			const valorCredito = parseFloat(haber) || 0;

			// Calcular el valor de "Debe-Haber"
			const debeHaber = valorDebito !== 0 ? -valorDebito : valorCredito;

			if (saldo instanceof String) {
				saldo = parseFloat(saldo.replace(",", ""));
			}

			// Retornar la fila procesada en el formato correcto
			return [fechaStr, numeroDoc, `${tipoTransaccion} - ${descripcion}`, debeHaber, saldo];
		});

		if (warningDate) message.info("Su plantilla utiliza un formato de fecha que puede causar problemas de integridad, por favor convierta las fechas a texto", 10);
		
		return [HEADER, ...resultado];

	} catch (error) {
		console.error("Error al procesar datos de Banco Industrial:", error);
		message.error("Error al procesar los datos de Banco Industrial. Verifique el formato de la plantilla.");
		return [];
	}
}

// Función para procesar datos de Banco Banrural
function __bancoBanrural(data) {
	try {
		// Verificar que el formato de datos sea correcto
		if (!Array.isArray(data) || data.length === 0 || !Array.isArray(data[0])) {
			message.error("La plantilla proporcionada no tiene el formato esperado para Banco Banrural.");
			return [];
		}

		// Encontrar el índice de la HEADER (donde empieza la tabla de transacciones)
		const indexCabecera = data.findIndex(fila => Array.isArray(fila) && fila.includes("Fecha"));
		if (indexCabecera === -1 || data.length <= indexCabecera + 1) {
			message.error("No se encontraron datos de transacciones en el formato esperado.");
			return [];
		}

		// Filtrar y procesar las filas que son transacciones reales
		const filas = data.slice(indexCabecera + 1).filter(fila => fila.length > 0 && fila[0] && fila[2]); // Ignorar filas vacías

		let warningDate = false;

		const resultado = filas.map((fila) => {
			const [fechaExcel, oficina, descripcion, referencia, secuencial, debito, credito] = [
				fila[0],               // Fecha (puede ser Excel o cadena)
				fila[1] ?? "",         // Oficina (no se usará, pero lo obtenemos)
				fila[2] ?? "",         // Descripción
				fila[3] ?? "",         // Referencia
				fila[4] ?? "",         // Secuencial
				//fila[5] ?? "",         // Secuencial
				fila[6] ?? 0,          // Débito (si existe)
				fila[7] ?? 0,           // Crédito (si existe)
				//fila[8] ?? 0           // Saldo
			];
			let saldo = fila[8] ?? 0;

			// Identificar si la fecha es un número (formato Excel) o una cadena (formato DD/MM/YYYY)
			let fechaStr;
			if (typeof fechaExcel === "number") {
				// Convertir desde el formato Excel a una fecha
				const fechaObj = __excelDateToJSDate(fechaExcel);
				fechaStr = fechaObj.toISOString().split("T")[0]; // Convertir a formato YYYY-MM-DD
				warningDate = true;
			} else if (typeof fechaExcel === "string") {
				// Asumimos que la fecha está en formato DD/MM/YYYY
				fechaStr = __convertDateStringToISO(fechaExcel);
			} else {
				throw new Error("Formato de fecha no soportado.");
			}

			// Convertir valores de crédito/débito a números
			const valorDebito = parseFloat(debito) || 0;
			const valorCredito = parseFloat(credito) || 0;

			// Calcular el valor de "Debe-Haber"
			const debeHaber = valorDebito !== 0 ? -valorDebito : valorCredito;

			if (saldo instanceof String) {
				saldo = parseFloat(saldo.replace(",", ""));
			}

			// Construir la descripción completa
			const descripcionCompleta = `${descripcion} | ${oficina} | Ref: ${referencia} | Sec: ${secuencial}`;

			// Retornar la fila procesada en el formato correcto
			return [fechaStr, referencia, descripcionCompleta.trim(), debeHaber, saldo];
		});

		if (warningDate) message.info("Su plantilla utiliza un formato de fecha que puede causar problemas de integridad, por favor convierta las fechas a texto", 10);
		
		return [HEADER, ...resultado];

	} catch (error) {
		console.error("Error al procesar datos de Banco Banrural:", error);
		message.error("Error al procesar los datos de Banco Banrural. Verifique el formato de la plantilla.");
		return [];
	}
}

function __normalizeString(str) {
	return str
		.normalize("NFD") // Normalize to "decomposed" form
		.replace(/[\u0300-\u036f]/g, "") // Remove diacritical marks
		.toLowerCase(); // Make lowercase for case-insensitive matching
}

function __otherBank(data) {
	try {
		if (!data || !Array.isArray(data) || data.length === 0) {
			message.error("El archivo no contiene datos válidos.");
			return [];
		}

		const COLUMN_NAMES = {
			fecha: ["Fecha", "Date"],
			referencia: ["Referencia", "Documento", "No. Doc", "Número Documento"],
			descripcion: ["Descripción", "Descripcion", "Descripcion", "DescripciÃ³n"],
			debito: ["Debito", "Débito", "Débitos", "Debit", "Debits", "Debe", "DÃ©bito"],
			credito: ["Credito", "Crédito", "Créditos", "Credit", "Credits", "Haber", "CrÃ©dito"],
			saldo: ["Balance", "Saldo Contable", "Saldo Disponible", "Saldo"],
		};

		let headerRowIndex = -1;
		let headerRow = null;

		// Step 1: Find the header row dynamically
		for (let i = 0; i < data.length; i++) {
			const row = data[i];
			if (Array.isArray(row)) {
				const containsKeyHeader = row.some((cell) =>
					Object.values(COLUMN_NAMES).flat().some((name) =>
						__normalizeString(cell?.toString()).includes(__normalizeString(name))
					)
				);

				if (containsKeyHeader) {
					headerRowIndex = i;
					headerRow = row;
					break;
				}
			}
		}

		if (headerRowIndex === -1) {
			message.error("No se pudo encontrar la fila de encabezados en el archivo.");
			return [];
		}

		// Step 2: Map column indices to their respective fields
		const columnMap = {};
		Object.keys(COLUMN_NAMES).forEach((key) => {
			const columnIndex = headerRow.findIndex((header) =>
				COLUMN_NAMES[key].some((name) =>
					__normalizeString(header).includes(__normalizeString(name))
				)
			);
			if (columnIndex !== -1) columnMap[key] = columnIndex;
		});

		// Step 3: Validate mandatory fields
		const mandatoryFields = ["fecha", "referencia", "descripcion", "debito", "credito"];
		const missingFields = mandatoryFields.filter((field) => !(field in columnMap));
		if (missingFields.length > 0) {
			message.error(
				`No se encontraron las siguientes columnas obligatorias: ${missingFields.join(", ")}.`
			);
			return [];
		}

		// Step 4: Process data rows after the header row
		const result = data.slice(headerRowIndex + 1).map((row) => {
			try {
				const fecha = row[columnMap.fecha];
				const referencia = row[columnMap.referencia];
				const descripcion = row[columnMap.descripcion];
				const debito = parseFloat(row[columnMap.debito] || 0) || 0;
				const credito = parseFloat(row[columnMap.credito] || 0) || 0;
				const saldo =
					columnMap.saldo !== undefined
						? parseFloat(row[columnMap.saldo] || 0) || 0
						: 0;

				// Format the date
				let formattedFecha;
				if (typeof fecha === "number") {
					formattedFecha = __excelDateToJSDate(fecha).toISOString().split("T")[0];
				} else if (typeof fecha === "string") {
					formattedFecha = __convertDateStringToISO(fecha);
				} else {
					throw new Error("Formato de fecha no soportado.");
				}

				// Calculate "Debe-Haber" value // Debit its negative // Credit its positive
				const debeHaber = credito === 0 ? -debito : credito;

				return [formattedFecha, referencia, descripcion.trim(), debeHaber, saldo];
			} catch (error) {
				console.warn("Error procesando fila:", row, error);
				return null;
			}
		});

		// Step 5: Filter out invalid rows and return with headers
		const finalResult = result.filter((row) => row !== null);
		return [HEADER, ...finalResult];
	} catch (error) {
		console.error("Error al procesar los datos de la nueva plantilla:", error);
		message.error("Error al procesar los datos de la nueva plantilla.");
		return [];
	}
}

// Función para exportar los datos a Excel
const __exportToExcel = (data, filename) => {
	try {
		const worksheet = XLSX.utils.aoa_to_sheet(data);
		const workbook = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(workbook, worksheet, "Datos");

		// Descargar el archivo Excel
		XLSX.writeFile(workbook, filename);
		message.success("Archivo exportado con éxito.");
	} catch (error) {
		console.error("Error al exportar el archivo Excel:", error);
		message.error("Ocurrió un error al exportar el archivo.");
	}
};

const __exportToExcelWithColors = (data, filename) => {
	try {
		const worksheet = XLSX.utils.aoa_to_sheet(data);
		const workbook = XLSX.utils.book_new();

		const applyStyles = (cell, bgColor, textColor = "000000", bold = false) => {
			cell.s = {
				fill: { fgColor: { rgb: bgColor } },
				font: { color: { rgb: textColor }, bold: bold },
			};
		};

		const range = XLSX.utils.decode_range(worksheet["!ref"]);

		for (let row = range.s.r; row <= range.e.r; row++) {
			for (let col = range.s.c; col <= range.e.c; col++) {
				const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
				const cell = worksheet[cellAddress];

				if (cell) {
					if (row === 0) {
						applyStyles(cell, "D9E1F2", "000000", true); // Light Blue Background, Bold Text
					} 
					else if (data[row][0].trim() === "Total:") {
						applyStyles(cell, "C6EFCE", "006100", true); // Green Background, Dark Green Text, Bold
					}
					else if (!data[row][0].startsWith(" ") && col === 0) {
						applyStyles(cell, "BDD7EE", "000000", true); // Blue Background, Bold
					}
					else if (typeof data[row][col] === "number" && data[row][col] < 0) {
						applyStyles(cell, "FFC7CE", "9C0006"); // Light Red Background, Dark Red Text
					}
					else if (typeof data[row][col] === "number" && data[row][col] > 0) {
						applyStyles(cell, "E2EFDA", "006100"); // Light Green Background, Dark Green Text
					}					
					else if (row > 0 && col > 0) {
						applyStyles(cell, "FFFFFF", "000000"); // White Background, Black Text
					}
				}
			}
		}

		XLSX.utils.book_append_sheet(workbook, worksheet, "Datos");
		XLSX.writeFile(workbook, filename);

		message.success("Archivo exportado con éxito.");
	} catch (error) {
		console.error("Error al exportar el archivo Excel:", error);
		message.error("Ocurrió un error al exportar el archivo.");
	}
};
